import React, { useState, useEffect } from 'react';
import { X, Pill, Plus, Trash2, Edit, Save, FileText, Loader } from 'lucide-react';
import Select from 'react-select';
import apiService from '../../services/api';
import { useMedicaments } from '../../hooks/useApi';

const PrescriptionsModal = ({ consultation, isOpen, onClose }) => {
  const { data: medicaments = [] } = useMedicaments();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [prescriptionToDelete, setPrescriptionToDelete] = useState(null);
  const [selectedMedicament, setSelectedMedicament] = useState(null);
  const [formData, setFormData] = useState({
    medication: '',
    dosage: '',
    frequency: '',
    duration: '',
    instructions: ''
  });
  const [errors, setErrors] = useState({});

  // Charger les prescriptions au montage et quand consultation change
  useEffect(() => {
    if (isOpen && consultation?.id) {
      const fetchData = async () => {
        try {
          setLoading(true);
          setError(null);
          const result = await apiService.getPrescriptionsByConsultation(consultation.id);
          if (result.data) {
            setPrescriptions(Array.isArray(result.data) ? result.data : []);
          }
        } catch (err) {
          console.error('Erreur lors du chargement des prescriptions:', err);
          setError('Erreur lors du chargement des prescriptions');
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [isOpen, consultation?.id]);

  const loadPrescriptions = async () => {
    if (!consultation?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getPrescriptionsByConsultation(consultation.id);
      if (result.data) {
        setPrescriptions(Array.isArray(result.data) ? result.data : []);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des prescriptions:', err);
      setError('Erreur lors du chargement des prescriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Créer les options pour le sélecteur de médicaments
  const medicamentOptions = medicaments.map(medicament => ({
    value: medicament.id,
    label: `${medicament.name} - ${medicament.strength} (${medicament.dosageForm})`,
    medicament: medicament
  }));

  const handleMedicamentChange = (selectedOption) => {
    setSelectedMedicament(selectedOption);
    setFormData(prev => ({
      ...prev,
      medication: selectedOption ? selectedOption.medicament.name : '',
      dosage: selectedOption ? selectedOption.medicament.strength : ''
    }));
    if (errors.medication) {
      setErrors(prev => ({ ...prev, medication: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.medication?.trim()) newErrors.medication = 'Le médicament est requis';
    if (!formData.dosage?.trim()) newErrors.dosage = 'La posologie est requise';
    if (!formData.frequency?.trim()) newErrors.frequency = 'La fréquence est requise';
    if (!formData.duration?.trim()) newErrors.duration = 'La durée est requise';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddPrescription = async (e) => {
    e.preventDefault();
    if (!validateForm() || !consultation?.id) return;

    try {
      setLoading(true);
      setError(null);
      
      const prescriptionData = {
        consultationId: consultation.id,
        medication: formData.medication,
        dosage: formData.dosage,
        frequency: formData.frequency,
        duration: formData.duration,
        instructions: formData.instructions || null
      };

      await apiService.createPrescription(prescriptionData);
      
      // Recharger les prescriptions
      await loadPrescriptions();
      
      // Réinitialiser le formulaire
      setFormData({
        medication: '',
        dosage: '',
        frequency: '',
        duration: '',
        instructions: ''
      });
      setSelectedMedicament(null);
      setErrors({});
    } catch (err) {
      console.error('Erreur lors de l\'ajout de la prescription:', err);
      setError('Erreur lors de l\'ajout de la prescription');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setPrescriptionToDelete(id);
  };

  const confirmDelete = async () => {
    if (!prescriptionToDelete) return;

    try {
      setLoading(true);
      setError(null);
      await apiService.deletePrescription(prescriptionToDelete);
      
      // Recharger les prescriptions
      await loadPrescriptions();
      setPrescriptionToDelete(null);
    } catch (err) {
      console.error('Erreur lors de la suppression de la prescription:', err);
      setError('Erreur lors de la suppression de la prescription');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setPrescriptionToDelete(null);
  };

  if (!isOpen || !consultation) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-full">
              <Pill className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Prescription Médicale</h2>
              <p className="text-gray-600">
                Consultation #{consultation.id} - {consultation.patient?.user?.name || 'Patient inconnu'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <div className="flex h-[calc(90vh-8rem)]">
          {/* Table des prescriptions */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-primary-600" />
                Prescriptions en cours
              </h3>
            </div>

            {/* Message d'erreur */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {/* Indicateur de chargement */}
            {loading ? (
              <div className="text-center py-12">
                <Loader className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-2" />
                <p className="text-gray-600">Chargement...</p>
              </div>
            ) : prescriptions.length === 0 ? (
              <div className="text-center py-12">
                <Pill className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune prescription</h3>
                <p className="text-gray-500">Commencez par ajouter une prescription à droite</p>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                        Médicament
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                        Posologie
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                        Fréquence
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                        Durée
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                        Instructions
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {prescriptions.map((prescription) => (
                      <tr key={prescription.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 min-w-[200px]">
                          {prescription.medication}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 min-w-[100px]">
                          {prescription.dosage}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 min-w-[120px]">
                          {prescription.frequency}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 min-w-[100px]">
                          {prescription.duration}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600 min-w-[200px]">
                          {prescription.instructions || '-'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium min-w-[100px]">
                          <button
                            onClick={() => handleDeleteClick(prescription.id)}
                            className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Formulaire latéral */}
          <div className="w-96 bg-gray-50 p-6 border-l border-gray-200 overflow-y-auto">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Plus className="h-5 w-5 mr-2 text-green-600" />
                Nouvelle Prescription
              </h3>
            </div>

            <form onSubmit={handleAddPrescription} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Médicament *
                </label>
                <Select
                  value={selectedMedicament}
                  onChange={handleMedicamentChange}
                  options={medicamentOptions}
                  placeholder="Sélectionner un médicament..."
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: errors.medication ? '#ef4444' : '#d1d5db',
                      '&:hover': {
                        borderColor: errors.medication ? '#ef4444' : '#9ca3af'
                      }
                    })
                  }}
                />
                {errors.medication && (
                  <p className="text-red-500 text-xs mt-1">{errors.medication}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Posologie *
                </label>
                <input
                  type="text"
                  name="dosage"
                  value={formData.dosage}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                    errors.dosage ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: 500mg"
                />
                {selectedMedicament && (
                  <p className="text-xs text-gray-500 mt-1">Prérempli depuis {selectedMedicament.medicament.name}</p>
                )}
                {errors.dosage && (
                  <p className="text-red-500 text-xs mt-1">{errors.dosage}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fréquence *
                </label>
                <input
                  type="text"
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                    errors.frequency ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: 3 fois par jour"
                />
                {errors.frequency && (
                  <p className="text-red-500 text-xs mt-1">{errors.frequency}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée *
                </label>
                <input
                  type="text"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                    errors.duration ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: 5 jours"
                />
                {errors.duration && (
                  <p className="text-red-500 text-xs mt-1">{errors.duration}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instructions
                </label>
                <textarea
                  name="instructions"
                  value={formData.instructions}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Ex: Prendre après les repas"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span>Ajout en cours...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Ajouter la Prescription</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Modal de confirmation de suppression */}
        {prescriptionToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-red-100 rounded-full mr-3">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Supprimer la prescription</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Êtes-vous sûr de vouloir supprimer cette prescription ? Cette action est irréversible.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={cancelDelete}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Suppression...' : 'Supprimer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrescriptionsModal;

